import { useQuery } from '@tanstack/react-query';
import type {
  ClientDashboardSummaryDto,
  ClientLibraryDto,
  ClientOptionsDto,
  ClientQueryRowDto,
  ClientReleasedDossierDto,
  ClientRequestCountsDto,
  ClientRequestDetailDto,
  ClientRequestSummaryDto,
  ClientRequestView,
  ClientSampleDto,
} from '@mexp/shared';
import { http } from '@/api/client';

export const portalKeys = {
  all: ['portal'] as const,
  options: ['portal', 'options'] as const,
  list: (view: ClientRequestView) => ['portal', 'list', view] as const,
  request: (id: string) => ['portal', 'request', id] as const,
  releases: ['portal', 'releases'] as const,
  library: ['portal', 'library'] as const,
  summary: ['portal', 'summary'] as const,
  queries: ['portal', 'queries'] as const,
  samples: ['portal', 'samples'] as const,
  sample: (id: string) => ['portal', 'sample', id] as const,
};

export function useClientOptions() {
  return useQuery({
    queryKey: portalKeys.options,
    queryFn: () => http.get<ClientOptionsDto>('/portal/options'),
    staleTime: 5 * 60_000,
  });
}

export function useClientRequests(view: ClientRequestView) {
  return useQuery({
    queryKey: portalKeys.list(view),
    queryFn: () =>
      http.get<{
        items: ClientRequestSummaryDto[];
        counts: ClientRequestCountsDto;
      }>('/portal/requests', { view }),
    placeholderData: (prev) => prev,
  });
}

export function useClientRequest(id: string) {
  return useQuery({
    queryKey: portalKeys.request(id),
    queryFn: () => http.get<ClientRequestDetailDto>(`/portal/requests/${id}`),
    enabled: !!id,
  });
}

/** Every version Makin released across the company's dossiers, latest release first. */
export function useClientReleases() {
  return useQuery({
    queryKey: portalKeys.releases,
    queryFn: () => http.get<{ items: ClientReleasedDossierDto[] }>('/portal/releases'),
    select: (d) => d.items,
  });
}

/** The company's filed dossiers and certificates, and Makin's accreditation certificates (from the Data Bank). */
export function useClientLibrary() {
  return useQuery({
    queryKey: portalKeys.library,
    queryFn: () => http.get<ClientLibraryDto>('/portal/library'),
    staleTime: 60_000,
  });
}

/** The dashboard's counts: dossiers, queries and compliance, certificates, artworks and 3D images. */
export function useClientSummary() {
  return useQuery({ queryKey: portalKeys.summary, queryFn: () => http.get<ClientDashboardSummaryDto>('/portal/summary') });
}

/** Every query the company raised on its dossiers, newest first. */
export function useClientQueries() {
  return useQuery({
    queryKey: portalKeys.queries,
    queryFn: () => http.get<{ items: ClientQueryRowDto[] }>('/portal/queries'),
    select: (d) => d.items,
  });
}

/** Every sample the company asked for with its requisitions, latest change first (stages and courier only). */
export function useClientSamples() {
  return useQuery({
    queryKey: portalKeys.samples,
    queryFn: () => http.get<{ items: ClientSampleDto[] }>('/portal/samples'),
    select: (d) => d.items,
  });
}

/** The sample asked for with one requisition, or null when none was. */
export function useClientSample(requestId: string) {
  return useQuery({
    queryKey: portalKeys.sample(requestId),
    queryFn: () => http.get<{ sample: ClientSampleDto | null }>(`/portal/requests/${requestId}/sample`),
    select: (d) => d.sample,
    enabled: !!requestId,
  });
}
